import { lazy, Suspense, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { useChat } from '../store';
import { pickConversationToSelect } from '../embed';
import { connectSocket, disconnectSocket, getSocket } from '../socket';
import { Sidebar } from '../components/Sidebar';
import { ChatPanel } from '../components/ChatPanel';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { IncomingCallToast } from '../components/IncomingCallToast';
import type { ActiveCallInfo } from '../components/CallModal';

// Lazy-loaded surfaces. Settings pulls qrcode.react (2FA + invite QR) and
// the entire admin / sessions / storage trees that most users never open
// in a session. CallModal pulls livekit-client + @livekit/components-react
// (~165KB gzipped on its own). Splitting both keeps first paint under
// ~125KB gzipped versus the ~290KB it was before this commit.
const Settings = lazy(() =>
  import('./Settings').then((m) => ({ default: m.Settings })),
);
const CallModal = lazy(() =>
  import('../components/CallModal').then((m) => ({ default: m.CallModal })),
);
const CreateChannelModalLazy = lazy(() =>
  import('../components/CreateChannelModal').then((m) => ({
    default: m.CreateChannelModal,
  })),
);
const ChannelSettingsModalLazy = lazy(() =>
  import('../components/ChannelSettingsModal').then((m) => ({
    default: m.ChannelSettingsModal,
  })),
);
import { CallsPage } from '../components/CallsPage';
import { ArchivedSidebar } from '../components/ArchivedSidebar';
import { IconNav, type NavView } from '../components/IconNav';
import { ConversationInfoPanel } from '../components/ConversationInfo/ConversationInfoPanel';
import { selectActiveConversation } from '../store';
import {
  ensureNotificationPermission,
  playNotificationChirp,
  showNotification,
} from '../utils/notifications';
import {
  isConversationMuted,
  loadNotificationPrefs,
} from '../utils/notificationPrefs';
import { useTabTitleBadge } from '../utils/tabTitle';
import type { Message, User } from '../types';

interface HomeProps {
  user: User;
  onLogout: () => void;
  initialConversationId?: string | null;
}

interface IncomingCallState {
  roomName: string;
  conversationId: string;
  fromUserId: string;
  fromDisplayName: string | null;
  fromEmail: string;
  video: boolean;
}

export function Home({ user, onLogout, initialConversationId }: HomeProps) {
  const setMe = useChat((s) => s.setMe);
  const setUsers = useChat((s) => s.setUsers);
  const setConversations = useChat((s) => s.setConversations);
  const upsertMessage = useChat((s) => s.upsertMessage);
  const markMessageDeleted = useChat((s) => s.markMessageDeleted);
  const addReaction = useChat((s) => s.addReaction);
  const removeReaction = useChat((s) => s.removeReaction);
  const setTyping = useChat((s) => s.setTyping);
  const setPresence = useChat((s) => s.setPresence);
  const setReadReceipt = useChat((s) => s.setReadReceipt);
  const bumpConversationLastMessage = useChat((s) => s.bumpConversationLastMessage);
  const applyConversationUpdate = useChat((s) => s.applyConversationUpdate);
  const upsertConversation = useChat((s) => s.upsertConversation);
  const removeConversation = useChat((s) => s.removeConversation);
  const setChannels = useChat((s) => s.setChannels);
  const upsertChannel = useChat((s) => s.upsertChannel);
  const removeChannel = useChat((s) => s.removeChannel);
  const setConversationChannel = useChat((s) => s.setConversationChannel);
  const reset = useChat((s) => s.reset);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeCall, setActiveCall] = useState<ActiveCallInfo | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCallState | null>(null);
  const [view, setView] = useState<NavView>('chats');
  // Real-time unread -> platform parent: push the live total to the embedding
  // Connect dock so its bubble badge + pulse update instantly (its own poll is
  // only a 30s fallback). Only meaningful when embedded (parent !== window).
  const unreadConversations = useChat((s) => s.conversations);
  useEffect(() => {
    if (window.parent === window) return;
    const total = unreadConversations.reduce((n, c) => n + (c.unreadCount || 0), 0);
    try {
      window.parent.postMessage({ type: 'pgoc-connect-unread', count: total }, '*');
    } catch { /* ignore cross-origin */ }
  }, [unreadConversations]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [createChannelOpen, setCreateChannelOpen] = useState(false);
  const [channelSettingsId, setChannelSettingsId] = useState<string | null>(null);
  const selectConversation = useChat((s) => s.selectConversation);
  const conversations = useChat((s) => s.conversations);
  const conversationsLoaded = useChat((s) => s.conversationsLoaded);
  const activeConversation = useChat(selectActiveConversation);

  // Tab title badge — shows "(N) Chat" when there are unread messages so
  // backgrounded tabs surface activity without the user switching to them.
  useTabTitleBadge();

  // PC-3 deep link: once conversations load, open the requested conversation - but only
  // if the user is a member (it appears in their list). Runs once per load.
  useEffect(() => {
    if (!conversationsLoaded || !initialConversationId) return;
    const target = pickConversationToSelect(
      initialConversationId,
      conversations.map((c) => c.id),
    );
    if (target) selectConversation(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationsLoaded, initialConversationId]);

  useEffect(() => {
    setMe(user);

    void (async () => {
      try {
        const [usersRes, convsRes, channelsRes] = await Promise.all([
          api.users(),
          api.conversations(),
          api.listChannels(),
        ]);
        setUsers(usersRes.users);
        setConversations(convsRes.conversations);
        setChannels(channelsRes.channels);
      } catch {
        /* swallow */
      }
    })();

    connectSocket();
    const socket = getSocket();

    socket.on('hello', () => {});
    socket.on('connect_error', (err: Error) => {
      // eslint-disable-next-line no-console
      console.warn('socket connect error', err.message);
    });

    socket.on('message:created', (msg: Message) => {
      upsertMessage(msg);
      bumpConversationLastMessage(msg);

      // Only notify when the user can't already see the message: skip if the
      // sender is me, the conversation is muted, the user is actively reading
      // this conversation in a focused tab, the conversation type's prefs say
      // not to, or notification permission isn't granted. The mention path
      // below handles its own notification, so messages with @-mentions of me
      // will get a separate, louder notification rather than a duplicate.
      const state = useChat.getState();
      if (msg.senderId === state.me?.id) return;
      const conv = state.conversations.find((c) => c.id === msg.conversationId);
      if (!conv) return;
      if (isConversationMuted(conv.mutedUntil)) return;
      const focused = !document.hidden && document.hasFocus();
      const looking = state.selectedConversationId === msg.conversationId;
      if (focused && looking) return;

      const prefs = loadNotificationPrefs();
      const groupPrefs = conv.type === 'dm' ? prefs.message : prefs.group;
      if (!groupPrefs.show) return;

      const sender = state.users.find((u) => u.id === msg.senderId);
      const senderName = sender?.displayName ?? sender?.email ?? 'New message';
      const where =
        conv.type === 'dm'
          ? ''
          : conv.name
            ? ` in ${conv.name}`
            : ' in a group';
      const title = `${senderName}${where}`;
      const body = prefs.preview ? msg.body ?? '' : '';
      // Coalesce per-conversation: a burst of messages collapses into one
      // OS-level notification rather than spamming the corner.
      showNotification(title, { body, tag: `chat:${msg.conversationId}` });
      if (groupPrefs.sound) playNotificationChirp();
    });
    socket.on('message:edited', (msg: Message) => {
      upsertMessage(msg);
    });
    socket.on(
      'message:deleted',
      (payload: { messageId: string; conversationId: string; deletedAt: string }) => {
        markMessageDeleted(payload.conversationId, payload.messageId, payload.deletedAt);
      },
    );
    socket.on(
      'reaction:added',
      (payload: { messageId: string; conversationId: string; userId: string; emoji: string }) => {
        addReaction(payload.conversationId, payload.messageId, payload.userId, payload.emoji);

        // Only notify when someone reacts to MY message and I'm not the
        // reactor; respect mute + the conversation type's reaction prefs.
        const state = useChat.getState();
        if (payload.userId === state.me?.id) return;
        const messages = state.messagesByConv[payload.conversationId] ?? [];
        const target = messages.find((m) => m.id === payload.messageId);
        if (!target || target.senderId !== state.me?.id) return;
        const conv = state.conversations.find((c) => c.id === payload.conversationId);
        if (!conv || isConversationMuted(conv.mutedUntil)) return;
        const focused = !document.hidden && document.hasFocus();
        const looking = state.selectedConversationId === payload.conversationId;
        if (focused && looking) return;
        const prefs = loadNotificationPrefs();
        const groupPrefs = conv.type === 'dm' ? prefs.message : prefs.group;
        if (!groupPrefs.reactions) return;
        const reactor = state.users.find((u) => u.id === payload.userId);
        const reactorName = reactor?.displayName ?? reactor?.email ?? 'Someone';
        showNotification(`${reactorName} reacted ${payload.emoji}`, {
          body: prefs.preview ? target.body ?? '' : '',
          tag: `chat:${payload.conversationId}`,
        });
        if (groupPrefs.sound) playNotificationChirp();
      },
    );
    socket.on(
      'reaction:removed',
      (payload: { messageId: string; conversationId: string; userId: string; emoji: string }) => {
        removeReaction(payload.conversationId, payload.messageId, payload.userId, payload.emoji);
      },
    );
    socket.on(
      'typing',
      (payload: { conversationId: string; userId: string; typing: boolean }) => {
        setTyping(payload.conversationId, payload.userId, payload.typing);
      },
    );
    socket.on('presence', (payload: { userId: string; online: boolean }) => {
      setPresence(payload.userId, payload.online);
    });
    socket.on(
      'read:receipt',
      (payload: { conversationId: string; userId: string; messageId: string }) => {
        setReadReceipt(payload.conversationId, payload.userId, payload.messageId);
      },
    );
    socket.on(
      'conversation:updated',
      (payload: { id: string; name: string | null; topic: string | null; avatarUpdatedAt?: string | null; advancedPrivacy?: boolean; participants: { id: string; email: string; displayName: string | null; role?: string }[] }) => {
        applyConversationUpdate(payload.id, {
          name: payload.name,
          topic: payload.topic,
          avatarUpdatedAt: payload.avatarUpdatedAt ?? null,
          advancedPrivacy: payload.advancedPrivacy,
          participants: payload.participants,
        });
      },
    );
    socket.on(
      'bridge:updated',
      (payload: {
        id: string;
        bridge: { network: string; displayPhoneNumber: string | null; lastInboundAt: string | null } | null;
      }) => {
        applyConversationUpdate(payload.id, { bridge: payload.bridge });
      },
    );
    socket.on(
      'conversation:joined',
      (payload: { id: string; type: 'dm' | 'group' | 'channel'; name: string | null; topic: string | null; avatarUpdatedAt?: string | null; advancedPrivacy?: boolean; participants: { id: string; email: string; displayName: string | null; role?: string }[] }) => {
        // Derive the user's own role from their entry in the
        // participants array. The previous hardcoded 'member' was
        // wrong for the creator of a group (who is the owner) — it
        // also broke any isAdmin gate that depended on myRole until
        // the next /api/conversations refresh.
        const meId = useChat.getState().me?.id;
        const ownEntry = meId
          ? payload.participants.find((p) => p.id === meId)
          : undefined;
        upsertConversation({
          id: payload.id,
          type: payload.type,
          name: payload.name,
          topic: payload.topic,
          avatarUpdatedAt: payload.avatarUpdatedAt ?? null,
          advancedPrivacy: payload.advancedPrivacy ?? false,
          participants: payload.participants,
          lastMessage: null,
          unreadCount: 0,
          lastReadId: null,
          myRole: ownEntry?.role ?? 'member',
          isFavorite: false,
          archivedAt: null,
          mutedUntil: null,
        channelId: null,
        });
        socket.emit('conversation:join', { conversationId: payload.id });
      },
    );
    socket.on(
      'conversation:left',
      (payload: { conversationId: string }) => {
        removeConversation(payload.conversationId);
      },
    );
    // Channel lifecycle. Created/updated arrive as full Channel DTOs;
    // deleted is just an id. The store handles list maintenance + the
    // "active filter is now gone" cleanup when the active channel
    // disappears.
    socket.on('channel:created', (payload: import('../types').Channel) => {
      upsertChannel(payload);
    });
    socket.on('channel:updated', (payload: import('../types').Channel) => {
      upsertChannel(payload);
    });
    socket.on('channel:deleted', (payload: { id: string }) => {
      removeChannel(payload.id);
    });
    // Conversation moved between channels — apply the new channelId so
    // sidebar filtering re-derives correctly.
    socket.on(
      'conversation:moved',
      (payload: { conversationId: string; channelId: string | null }) => {
        setConversationChannel(payload.conversationId, payload.channelId);
      },
    );
    // Pin / unpin propagation: update the global pinnedMessageIds store
    // directly so MessageBubble.isPinned stays correct even when the
    // user is looking at a different conversation. PinnedStrip listens
    // for chat:pin-changed for its own refresh — the strip is
    // conversation-scoped, but the store entry is global.
    socket.on(
      'message:pinned',
      (payload: { conversationId: string; messageId: string; pinnedBy: string }) => {
        useChat.getState().togglePinLocal(payload.messageId, true);
        window.dispatchEvent(
          new CustomEvent('chat:pin-changed', {
            detail: {
              conversationId: payload.conversationId,
              messageId: payload.messageId,
              pinned: true,
            },
          }),
        );
      },
    );
    socket.on(
      'message:unpinned',
      (payload: { conversationId: string; messageId: string }) => {
        useChat.getState().togglePinLocal(payload.messageId, false);
        window.dispatchEvent(
          new CustomEvent('chat:pin-changed', {
            detail: {
              conversationId: payload.conversationId,
              messageId: payload.messageId,
              pinned: false,
            },
          }),
        );
      },
    );
    socket.on(
      'message:mention',
      (payload: { messageId: string; conversationId: string; fromUserId: string; preview: string | null }) => {
        const state = useChat.getState();
        const focused = !document.hidden;
        const looking = state.selectedConversationId === payload.conversationId;
        if (focused && looking) return;

        const sender = state.users.find((u) => u.id === payload.fromUserId);
        const conv = state.conversations.find((c) => c.id === payload.conversationId);
        const senderName = sender?.displayName ?? sender?.email ?? 'Someone';
        const where =
          conv?.type === 'dm' ? '' : conv?.name ? ` in ${conv.name}` : ' in a group';

        // Same tag as message:created so the mention notification REPLACES
        // the regular new-message notification when both arrive — louder
        // copy, single notification corner footprint.
        showNotification(`${senderName} mentioned you${where}`, {
          body: payload.preview ?? '',
          tag: `chat:${payload.conversationId}`,
          requireInteraction: true,
        });
      },
    );

    // Security signals — fired by the server on a new sign-in or password
    // change for THIS user. Other connected sessions get the notification;
    // the new session itself isn't subscribed yet so it doesn't notify
    // about its own action.
    socket.on(
      'security:new-session',
      (payload: { ip: string | null; userAgent: string | null; at: string }) => {
        const prefs = loadNotificationPrefs();
        if (!prefs.security) return;
        const where = payload.ip ? ` from ${payload.ip}` : '';
        showNotification('New sign-in to your account', {
          body: `Someone just signed in${where}. If it wasn't you, change your password and review sessions.`,
          tag: `security:${payload.at}`,
          requireInteraction: true,
        });
      },
    );
    socket.on('security:password-changed', () => {
      const prefs = loadNotificationPrefs();
      if (!prefs.security) return;
      showNotification('Your password just changed', {
        body: "If that wasn't you, sign out everywhere and contact PGOC IT immediately.",
        tag: 'security:password-changed',
        requireInteraction: true,
      });
    });

    socket.on(
      'call:incoming',
      (payload: {
        roomName: string;
        conversationId: string;
        fromUserId: string;
        fromDisplayName: string | null;
        fromEmail: string;
        video: boolean;
      }) => {
        // Don't ring myself if I'm somehow the caller (defensive).
        if (payload.fromUserId === user.id) return;
        // If we're already in a call, ignore the incoming.
        setIncomingCall((prev) => (prev || activeCall ? prev : payload));
        showNotification(
          `${payload.fromDisplayName ?? payload.fromEmail} is calling`,
          { tag: `call:${payload.roomName}`, requireInteraction: true },
        );
      },
    );

    socket.on(
      'call:ended',
      (payload: { roomName: string; conversationId: string }) => {
        setIncomingCall((prev) =>
          prev?.roomName === payload.roomName ? null : prev,
        );
        setActiveCall((prev) =>
          prev?.roomName === payload.roomName ? null : prev,
        );
      },
    );

    void ensureNotificationPermission();

    return () => {
      socket.off('hello');
      socket.off('connect_error');
      socket.off('message:created');
      socket.off('message:edited');
      socket.off('message:deleted');
      socket.off('reaction:added');
      socket.off('reaction:removed');
      socket.off('typing');
      socket.off('presence');
      socket.off('read:receipt');
      socket.off('conversation:updated');
      socket.off('bridge:updated');
      socket.off('conversation:joined');
      socket.off('conversation:left');
      socket.off('conversation:moved');
      socket.off('channel:created');
      socket.off('channel:updated');
      socket.off('channel:deleted');
      socket.off('message:mention');
      socket.off('message:pinned');
      socket.off('message:unpinned');
      socket.off('security:new-session');
      socket.off('security:password-changed');
      socket.off('call:incoming');
      socket.off('call:ended');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user,
    setMe,
    setUsers,
    setConversations,
    upsertMessage,
    markMessageDeleted,
    addReaction,
    removeReaction,
    setTyping,
    setPresence,
    setReadReceipt,
    bumpConversationLastMessage,
    applyConversationUpdate,
    upsertConversation,
    removeConversation,
  ]);

  async function handleLogout() {
    disconnectSocket();
    try {
      await api.logout();
    } catch (err) {
      if (!(err instanceof ApiError)) {
        // ignore network errors during logout
      }
    }
    reset();
    onLogout();
  }

  return (
    <div className="h-screen flex">
      <IconNav
        view={view}
        onView={(v) => setView(v)}
        onOpenSettings={() => setSettingsOpen(true)}
        onLogout={handleLogout}
        onCreateChannel={() => setCreateChannelOpen(true)}
        onOpenChannelSettings={(id) => setChannelSettingsId(id)}
      />
      {view === 'chats' && <Sidebar />}
      {view === 'archived' && (
        <ArchivedSidebar
          onOpenChat={(id) => {
            selectConversation(id);
            setView('chats');
          }}
        />
      )}
      {view === 'calls' ? (
        <CallsPage onStartCall={(info) => setActiveCall(info)} />
      ) : (
        <ChatPanel
          onStartCall={(info) => setActiveCall(info)}
          onOpenInfo={() => setInfoOpen(true)}
        />
      )}
      {view !== 'calls' && infoOpen && activeConversation && (
        <ErrorBoundary
          label="the info panel"
          fallback={(error, reset) => (
            <CrashOverlay
              label="info panel"
              error={error}
              onClose={() => {
                reset();
                setInfoOpen(false);
              }}
            />
          )}
        >
          <ConversationInfoPanel
            conversation={activeConversation}
            onClose={() => setInfoOpen(false)}
            onLeft={() => {
              setInfoOpen(false);
              selectConversation(null);
            }}
            onStartCall={(info) => setActiveCall(info)}
            onOpenSearch={() => window.dispatchEvent(new CustomEvent('chat:open-search'))}
          />
        </ErrorBoundary>
      )}
      {settingsOpen && (
        <ErrorBoundary
          label="settings"
          fallback={(error, reset) => (
            <CrashOverlay
              label="settings"
              error={error}
              onClose={() => {
                reset();
                setSettingsOpen(false);
              }}
            />
          )}
        >
          <Suspense fallback={null}>
            <Settings
              onClose={() => setSettingsOpen(false)}
              onForceLogout={handleLogout}
            />
          </Suspense>
        </ErrorBoundary>
      )}
      {activeCall && (
        <ErrorBoundary
          label="the call panel"
          fallback={(error, reset) => (
            <CrashOverlay
              label="call panel"
              error={error}
              onClose={() => {
                reset();
                setActiveCall(null);
              }}
            />
          )}
        >
          <Suspense fallback={null}>
            <CallModal call={activeCall} onLeave={() => setActiveCall(null)} />
          </Suspense>
        </ErrorBoundary>
      )}
      {incomingCall && !activeCall && (
        <IncomingCallToast
          fromName={incomingCall.fromDisplayName ?? incomingCall.fromEmail}
          conversationId={incomingCall.conversationId}
          roomName={incomingCall.roomName}
          video={incomingCall.video}
          onAccept={(info) => {
            setIncomingCall(null);
            setActiveCall(info);
          }}
          onDecline={() => setIncomingCall(null)}
        />
      )}
      {createChannelOpen && (
        <Suspense fallback={null}>
          <CreateChannelModalLazy onClose={() => setCreateChannelOpen(false)} />
        </Suspense>
      )}
      {channelSettingsId && (
        <Suspense fallback={null}>
          <ChannelSettingsModalLazy
            channelId={channelSettingsId}
            onClose={() => setChannelSettingsId(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

function CrashOverlay({
  label,
  error,
  onClose,
}: {
  label: string;
  error: Error;
  onClose: () => void;
}) {
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl border border-rose-500/30 bg-zinc-950 p-5 shadow-2xl"
      >
        <p className="text-[10px] uppercase tracking-[0.16em] text-rose-300 mb-1">
          Crashed
        </p>
        <p className="text-base font-semibold tracking-tight">
          The {label} hit an error.
        </p>
        <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
          The rest of the app is still working. Closing this and re-opening it
          usually clears the issue. If it persists, send the build hash from
          Settings → Help to PGOC IT.
        </p>
        <p className="mt-3 text-[11px] text-zinc-500 font-mono break-words">
          {error.message || 'Unknown error.'}
        </p>
        <button
          onClick={onClose}
          className="mt-4 w-full rounded-md bg-zinc-900 hover:bg-zinc-800 hairline text-zinc-200 text-sm px-3 py-2 transition"
        >
          Close
        </button>
      </div>
    </div>
  );
}
